import { useSession } from '@/auth/useSession';
import { InternalNavLink } from '@/routes/_layout/-components/nav-link';
import { Link, useMatchRoute } from '@tanstack/react-router';
import { shouldRenderItem } from './should-render-item';
import clsx from 'clsx';

type RequiredTabPageProps = Pick<InternalNavLink, 'label' | 'linkProps'>;
type OptionalTabPageProps = Partial<Pick<InternalNavLink, 'iconComponent' | 'shouldRender'>>;

export interface TabPageDefinition extends RequiredTabPageProps, OptionalTabPageProps {
  isSelectedTab?: boolean;
}

export interface TabPageProps {
  tabs: TabPageDefinition[];
  children?: React.ReactNode;
}

export function TabPage({ tabs, children }: TabPageProps) {
  const { actingUser } = useSession();
  const matchRoute = useMatchRoute();

  return (
    <>
      <div className="flex flex-wrap gap-4 text-sm my-2">
        {tabs.map((tab, index) => {
          const { label, iconComponent: Icon, linkProps, shouldRender, isSelectedTab } = tab;
          const visibleItem = shouldRenderItem(actingUser, shouldRender);

          if (!visibleItem) return null;

          const { to, params, search } = linkProps;
          const isActive = !!matchRoute(linkProps) || isSelectedTab;

          return (
            <Link
              key={`tab-${index}`}
              to={to}
              params={params}
              search={search}
              className={clsx('px-2', {
                'font-bold border-b-2 border-black': isActive,
                'text-gray-600': !isActive,
              })}
            >
              <div className="flex items-center gap-x-2">
                {Icon && <Icon className="h-4 w-4 flex-shrink-0" />}
                {label}
              </div>
            </Link>
          );
        })}
      </div>
      {children && <div className="w-full flex-1 min-h-0">{children}</div>}
    </>
  );
}
